import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Minus,
  Calendar,
  AlertCircle,
  Brain
} from 'lucide-react';

export default function AssessmentComparison() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);

  const type = searchParams.get('type');
  const a1 = searchParams.get('a1');
  const a2 = searchParams.get('a2');

  useEffect(() => {
    if (type && a1 && a2) {
      fetchComparison();
    }
  }, [type, a1, a2]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/dashboard/assessments/compare', {
        params: { type, assessment1_id: a1, assessment2_id: a2 }
      });
      setComparison(response.data);
    } catch (error) {
      toast.error('Failed to load comparison');
    } finally {
      setLoading(false);
    }
  };

  const getChangeIcon = (before, after) => {
    if (before === after) return <Minus className="w-4 h-4 text-muted-foreground" />;
    return <ArrowRight className="w-4 h-4 text-amber-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (!comparison) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Unable to load comparison</p>
      </div>
    );
  }

  const { assessment1, assessment2, changes } = comparison;
  const Icon = type === 'fear' ? AlertCircle : Brain;
  const colorClass = type === 'fear' ? 'text-blue-600 bg-blue-100' : 'text-purple-600 bg-purple-100';

  return (
    <div className="min-h-screen bg-background" data-testid="assessment-comparison">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container-custom py-4 flex items-center gap-4">
          <button
            onClick={() => (window.location.href = '/dashboard/assessments')}
            className="p-2 hover:bg-muted rounded-[var(--radius)] transition-smooth"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold capitalize">{type} Assessment Comparison</h1>
              <p className="text-sm text-muted-foreground">See how your results have changed over time</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container-custom py-8">
        {/* Date Comparison Header */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Earlier Assessment</p>
                  <p className="font-semibold">{formatDate(assessment1.completedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Later Assessment</p>
                  <p className="font-semibold">{formatDate(assessment2.completedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side by Side Comparison */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Results Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Earlier Results */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <h3 className="font-semibold mb-4 text-center border-b pb-2">Earlier</h3>
                {type === 'fear' ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Primary Edge</p>
                      <p className="font-medium text-lg">{assessment1.results?.primary_fear_cluster || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Intensity</p>
                      <p className="font-medium">{assessment1.results?.fear_intensity || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Secondary Edges</p>
                      <p className="font-medium">{assessment1.results?.secondary_fears?.join(', ') || '-'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Stated Archetype</p>
                      <p className="font-medium text-lg">{assessment1.results?.primary_archetype || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Core Archetype</p>
                      <p className="font-medium">{assessment1.results?.secondary_archetype || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Leadership Style</p>
                      <p className="font-medium">{assessment1.results?.leadership_style || '-'}</p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-4">
                  {assessment1.questionCount} questions answered
                </p>
              </div>

              {/* Later Results */}
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <h3 className="font-semibold mb-4 text-center border-b pb-2">Later (Current)</h3>
                {type === 'fear' ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Primary Edge</p>
                      <p className="font-medium text-lg">{assessment2.results?.primary_fear_cluster || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Intensity</p>
                      <p className="font-medium">{assessment2.results?.fear_intensity || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Secondary Edges</p>
                      <p className="font-medium">{assessment2.results?.secondary_fears?.join(', ') || '-'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Stated Archetype</p>
                      <p className="font-medium text-lg">{assessment2.results?.primary_archetype || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Core Archetype</p>
                      <p className="font-medium">{assessment2.results?.secondary_archetype || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Leadership Style</p>
                      <p className="font-medium">{assessment2.results?.leadership_style || '-'}</p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-4">
                  {assessment2.questionCount} questions answered
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Changes Summary */}
        <Card>
          <CardHeader>
            <CardTitle>What Changed?</CardTitle>
          </CardHeader>
          <CardContent>
            {changes.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Minus className="w-8 h-8 text-green-600" />
                </div>
                <p className="font-medium text-lg">No significant changes detected</p>
                <p className="text-muted-foreground mt-1">
                  Your results have remained consistent between these two assessments.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {changes.map((change, index) => (
                  <div 
                    key={index}
                    className="flex items-center p-4 bg-amber-50 border border-amber-200 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{change.field}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-muted-foreground">{change.before || 'None'}</span>
                        <ArrowRight className="w-4 h-4 text-amber-500" />
                        <span className="font-medium text-amber-700">{change.after || 'None'}</span>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                      Changed
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-center gap-4 mt-8">
          <Button variant="outline" onClick={() => window.location.href = '/dashboard/assessments'}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Assessments
          </Button>
          <Button onClick={() => window.location.href = `/results/${assessment2.id}`}>
            View Latest Results
          </Button>
        </div>
      </main>
    </div>
  );
}
